/**
 * Projects API
 */

import { Project, ProjectCreate } from '../types/api';
import { getAPIClient } from './client';
import { getApiBaseUrl } from '../utils/env';

export interface ProjectStats {
  project_id: string;
  project_name: string;
  total_artifacts: number;
  artifacts_by_type: Record<string, number>;
  total_artifact_size_mb: number;
  total_entities: number;
  entities_by_type: Record<string, number>;
  total_chunks: number;
  chunks_by_artifact_type: Record<string, number>;
  total_sources: number;
  sources_by_type: Record<string, number>;
  total_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
  running_jobs: number;
  created_at: string;
  oldest_artifact: string | null;
  newest_artifact: string | null;
}

/**
 * Check if we're running against an on-premise deployment
 * On-premise deployments have issues with query parameters when X-Tenant-ID header is present
 */
function isOnPremiseDeployment(): boolean {
  const deploymentType = import.meta.env.VITE_DEPLOYMENT_TYPE;
  if (deploymentType === 'onprem' || deploymentType === 'client-onprem') {
    return true;
  }

  // Also check the API base URL for on-premise patterns
  const apiUrl = getApiBaseUrl();
  const isLocalhost = apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1');
  const isPrivateIP = /(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(apiUrl);
  const isInternalDomain = /\.(local|internal|lan|corp)/.test(apiUrl);

  return isLocalhost || isPrivateIP || isInternalDomain;
}

export async function listProjects(tenantId?: string, options?: { includeArchived?: boolean }): Promise<Project[]> {
  const client = getAPIClient();
  const isOnPrem = isOnPremiseDeployment();
  const params: Record<string, string | boolean> = {};
  if (options?.includeArchived) params.include_archived = true;

  if (isOnPrem) {
    // WORKAROUND for on-premise: Call /projects without query params to avoid hanging (except include_archived)
    const raw = await client.get<Project[]>('/projects', { params: Object.keys(params).length ? params : undefined });
    const allProjects = Array.isArray(raw) ? raw : [];
    let list = tenantId ? allProjects.filter(p => p.tenant_id === tenantId) : allProjects;
    // Prefer "Production" project first so default/selector picks it
    list = [...list].sort((a, b) => {
      const aProd = a.name && a.name.includes('Production') ? 1 : 0;
      const bProd = b.name && b.name.includes('Production') ? 1 : 0;
      return bProd - aProd;
    });
    return list;
  } else {
    const requestParams = { ...params, ...(tenantId ? { tenant_id: tenantId } : {}) };
    const raw = await client.get<Project[]>('/projects', { params: requestParams });
    const list = Array.isArray(raw) ? raw : [];
    return [...list].sort((a, b) => {
      const aProd = a.name && a.name.includes('Production') ? 1 : 0;
      const bProd = b.name && b.name.includes('Production') ? 1 : 0;
      return bProd - aProd;
    });
  }
}

export async function getProject(projectId: string): Promise<Project> {
  const client = getAPIClient();
  return client.get<Project>(`/projects/${projectId}`);
}

export async function getProjectStats(projectId: string): Promise<ProjectStats> {
  const client = getAPIClient();
  return client.get<ProjectStats>(`/projects/${projectId}/stats`, {
    params: { project_id: projectId }
  });
}

export async function createProject(data: ProjectCreate): Promise<Project> {
  const client = getAPIClient();
  return client.post<Project>('/projects', data);
}

export async function updateProject(projectId: string, data: Partial<ProjectCreate>): Promise<Project> {
  const client = getAPIClient();
  return client.patch<Project>(`/projects/${projectId}`, data);
}

/** Archive a project (soft). Archived projects are excluded from listProjects by default. */
export async function archiveProject(projectId: string): Promise<Project> {
  const client = getAPIClient();
  return client.post<Project>(`/projects/${projectId}/archive`, {});
}

export async function deleteProject(projectId: string): Promise<void> {
  const client = getAPIClient();
  return client.delete(`/projects/${projectId}`);
}


